import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import Avatar from '../Avatar';
import CompassHint from './CompassHint';
import {
  CENTER,
  EASE,
  PEER_AVATAR_R,
  PEER_BADGE_OFFSET,
  PEER_BADGE_R,
  PEER_HIT_R,
  PEER_SKIN,
  PEER_TRACKED_RING_R,
  RING_BASE,
  RING_BRIGHT,
  RING_DIM,
  VIEWBOX,
  WRAPPER_ASPECT,
  stalenessSubtextColor,
} from './radarConstants';
import { iconMarkup } from '../../lib/map/mapIcons';
import { KIND_DISPLAY } from '../../lib/map/festivalMapGeoJson';
import { formatFindDistanceWithWalkTime } from '../../lib/proximity/engine/findDistanceFormat';
import { AMENITY_REACH_M } from '../../lib/map/amenityFinder';
import { getProfilePhotoUrl } from '../../lib/images/profilePhotoUrl';
import { getAvatarColorHex } from '../Avatar';
import {
  faceStyle,
  glassFaces,
  ringColor,
  selStyle,
  useSelfLook,
  washColor,
} from '../../lib/map/faceStyle';
import { selfPinCanvas } from '../../lib/map3d/pinDraft';
import markUrl from '../../assets/brand/prii-mark.png';
import {
  PUCK,
  pickClusterRepresentatives as pickSharedRepresentatives,
} from '../../lib/map/peerPuck';
import {
  clusterIdFromPeers,
  computeClusters,
  computeTargetArrow,
  createPeerMotion,
  formatFindPeerDistanceWithWalkTime,
  formatStalenessShort,
  isPeerMotionSettled,
  logTargetArrowDiagnostics,
  mapFindRowsToRadarPeers,
  motionScreenPosition,
  RADAR_CLUSTER_THRESHOLD,
  RADAR_RING_OUTER,
  resolveRadarTarget,
  ringHighlightsForZone,
  ringZoneForDistance,
  shouldShowTargetArrow,
  bleNearbyLabel,
  stepPeerMotion,
  targetArrowRoomFactor,
} from '../../lib/proximity/runtime';

import {
  FIND_PEER_FALLBACK_LABEL,
  findPeerInitial,
  isUuidLikeString,
} from '../../lib/proximity/findPeerDisplayName';

/**
 * A friend's ring (Sam's 6, 2026-10-26): the amenities' white hairline, plus a soft glow in the friend's own colour -
 * people pop, places stay flat.
 */
/**
 * The arrow (Sam's 4, 2026-10-26): today's flowing dashes made stronger - full strength, a little bolder, a rounder
 * bigger head - in the centre dot's own purple so it leaves the dot as one colour, with a thin dark-purple edge that keeps
 * each dash crisp over the rings. (The Maps navigation arrow, lib/map3d/peopleLayer ARROW, keeps its own look for now.)
 */
const ARROW_HEAD = { tipSize: 10, tipWidth: 7.5 };

function faceGlow(hex) {
  const n = parseInt(hex.slice(1), 16);
  return `rgb(${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255} / 0.55)`;
}

function TrackedHalo({ style, colour }) {
  const r = PEER_AVATAR_R;
  return (
    <>
      {(style === 'b' || style === 'd') && (
        <circle
          r={r + (style === 'd' ? 3.5 : 1)}
          fill={colour}
          style={{ filter: `drop-shadow(0 0 7px ${colour}) drop-shadow(0 0 3px ${colour})` }}
        />
      )}
      {(style === 'c' || style === 'd') && <circle r={r + 3} fill="#fff" fillOpacity={0.95} />}
    </>
  );
}

function RadarPeerFace({ photoUrl, displayName, cluster = false, radius = PEER_AVATAR_R }) {
  const clipId = useId();
  const safeName = isUuidLikeString(displayName)
    ? FIND_PEER_FALLBACK_LABEL
    : displayName || FIND_PEER_FALLBACK_LABEL;
  const src = !cluster && photoUrl ? getProfilePhotoUrl(photoUrl, PUCK.photoPx) : null;
  if (glassFaces()) {
    // the wash and the friend's colour ring, like every other face (lib/map/faceStyle.js)
    const c = ringColor(safeName, cluster),
      style = faceStyle(),
      w = Math.max(1.5, radius * 0.13),
      inner = radius - w;
    return (
      <>
        <defs>
          <clipPath id={clipId}>
            <circle r={inner} />
          </clipPath>
        </defs>
        {style === 'halo' && (
          <circle
            r={radius + 0.6}
            fill="none"
            stroke="#fff"
            strokeOpacity={0.9}
            strokeWidth={1.2}
          />
        )}
        <circle
          r={radius - w / 2}
          fill={washColor(c)}
          stroke={c}
          strokeWidth={w}
          style={
            style === 'glow' ? { filter: `drop-shadow(0 0 ${radius * 0.3}px ${c})` } : undefined
          }
        />
        {src ? (
          <image
            href={src}
            x={-inner}
            y={-inner}
            width={inner * 2}
            height={inner * 2}
            clipPath={`url(#${clipId})`}
            preserveAspectRatio="xMidYMid slice"
            pointerEvents="none"
          />
        ) : (
          !cluster && (
            <text
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={PUCK.initial.size + 2}
              fontWeight={700}
              fill="#fff"
              pointerEvents="none"
            >
              {findPeerInitial(safeName)}
            </text>
          )
        )}
      </>
    );
  }
  const fill = cluster ? PUCK.clusterFill : getAvatarColorHex(safeName);
  const strokeWidth = cluster ? PUCK.clusterStrokeWidth : PEER_SKIN.strokeWidth;
  const casing = (
    <circle
      r={PEER_SKIN.casingR}
      fill={PEER_SKIN.casing}
      style={
        cluster
          ? undefined
          : { filter: `drop-shadow(0 0 3px ${faceGlow(getAvatarColorHex(safeName))})` }
      }
    />
  );
  const face = cluster ? (
    <circle r={radius} fill={fill} stroke={PEER_SKIN.stroke} strokeWidth={strokeWidth} />
  ) : (
    <circle r={radius} fill={fill} stroke="rgb(255 255 255 / 0.6)" strokeWidth={0.9} />
  );

  if (!src) {
    return (
      <>
        {casing}
        {face}
        {!cluster && (
          <text
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={PUCK.initial.size}
            fontWeight={PUCK.initial.weight}
            letterSpacing={PUCK.initial.spacing}
            fill={PUCK.initial.fill}
            pointerEvents="none"
          >
            {findPeerInitial(safeName)}
          </text>
        )}
      </>
    );
  }

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <circle r={radius} />
        </clipPath>
      </defs>
      {casing}
      {face}
      <image
        href={src}
        x={-radius}
        y={-radius}
        width={radius * 2}
        height={radius * 2}
        clipPath={`url(#${clipId})`}
        preserveAspectRatio="xMidYMid slice"
        pointerEvents="none"
      />
    </>
  );
}

/**
 * Amenities a Find chip put on the radar (Stage 6, Sam 2026-10-26) are rows like friends - placed, clustered, tapped and
 * tracked the same way, the arrow included - drawn as the map's disc (its colour and glyph) inside the avatar's own
 * ring. The radar's scale ends at 60 m and amenities are mostly further, so distance also reads as fade: full strength
 * close, down to 40% at the edge of reach; the tracked one never fades.
 */
const AMENITY_FAR_OPACITY = 0.4;
/** Smaller than a friend's face (18): a place matters less than a person (Sam, 2026-10-26). */
const AMENITY_R = 12;
const AMENITY_TRACKED_RING_R = AMENITY_R + 4;
const AMENITY_HIT_R = AMENITY_R + 6; // 36 units across: still a comfortable fingertip at the radar's scale
function amenityOpacity(peer) {
  if (peer.isTracked || peer.distanceMeters == null) return 1;
  const t = Math.min(1, Math.max(0, (peer.distanceMeters - 60) / (AMENITY_REACH_M - 60)));
  return 1 - t * (1 - AMENITY_FAR_OPACITY);
}
/**
 * The map's own disc (Sam's b, 2026-10-26): the kind's colour, a white glyph, and a thin white ring - as thin as a map
 * chip's edge - so a place never reads as a friend's face, whose ring is purple, even when their colours match.
 */
function AmenityFace({ kind }) {
  const r = AMENITY_R;
  return (
    <>
      <circle r={r + 1.2} fill={PEER_SKIN.casing} />
      <circle
        r={r}
        fill={KIND_DISPLAY[kind]?.color ?? '#8b7fa8'}
        stroke="rgb(255 255 255 / 0.6)"
        strokeWidth={0.9}
      />
      <g
        transform="translate(-7 -7)"
        color="#ffffff"
        pointerEvents="none"
        dangerouslySetInnerHTML={{ __html: iconMarkup(kind, 14) }}
      />
    </>
  );
}

const BAND_DEPTH = [0.4, 0.58, 0.76];

function resolveRingBand(base, level) {
  const mod = level === 'bright' ? RING_BRIGHT : level === 'dim' ? RING_DIM : null;
  const baseStrokeWidth = mod?.strokeWidth ?? 0.5;
  const baseStrokeOpacity = mod?.strokeOpacity ?? base.strokeOpacity;
  return {
    r: base.r,
    strokeWidth: baseStrokeWidth * 1.4,
    strokeOpacity: Math.min(0.9, baseStrokeOpacity + 0.14),
  };
}

/** Bright-ring hand-over time: the old ring fades down while the new one fades up. */
const RING_CROSSFADE = '700ms ease-in-out';

function Rings({ distanceMeters }) {
  const gradientId = useId().replace(/:/g, '');
  // Held between renders: a friend standing on the 5 m line must not flick the bright ring back and forth.
  const zoneRef = useRef(null);
  const zone = useMemo(() => {
    const next = ringZoneForDistance(distanceMeters, zoneRef.current);
    zoneRef.current = next;
    return next;
  }, [distanceMeters]);

  const bands = useMemo(() => {
    const highlights = ringHighlightsForZone(zone);
    return RING_BASE.map((base) => {
      const highlight = highlights[base.key];
      const level = highlight === 'none' ? null : highlight;
      return { base, level, ...resolveRingBand(base, level) };
    });
  }, [zone]);

  return (
    <>
      <defs>
        <linearGradient id={`${gradientId}-ringFill`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(138,69,184)" stopOpacity="0.17" />
          <stop offset="100%" stopColor="rgb(95,42,143)" stopOpacity="0.12" />
        </linearGradient>
        <radialGradient id={`${gradientId}-ringGlow`} cx="0.5" cy="1.18" r="0.8">
          <stop offset="0%" stopColor="rgb(172,105,215)" stopOpacity="0.58" />
          <stop offset="72%" stopColor="rgb(140,72,192)" stopOpacity="0" />
        </radialGradient>
        {RING_BASE.map((ring, i) => (
          <clipPath key={ring.key} id={`${gradientId}-ringClip${i}`}>
            <circle cx={CENTER} cy={CENTER} r={ring.r} />
          </clipPath>
        ))}
      </defs>

      <g style={{ animation: 'glow-breathe 5.2s ease-in-out infinite' }}>
        {bands.map((band, i) => {
          const state = 0.45 + (band.strokeOpacity - 0.18) * 1.2;
          return (
            <g
              key={`bloom-${band.base.key}`}
              clipPath={`url(#${gradientId}-ringClip${i})`}
              style={{
                opacity: Math.min(0.88, state * 0.92),
                transition: `opacity ${RING_CROSSFADE}`,
              }}
            >
              <circle cx={CENTER} cy={CENTER} r={band.r} fill={`url(#${gradientId}-ringFill)`} />
              <circle
                cx={CENTER}
                cy={CENTER}
                r={band.r}
                fill={`url(#${gradientId}-ringGlow)`}
                style={{
                  opacity: BAND_DEPTH[i] * (0.6 + state * 0.5),
                  transition: `opacity ${RING_CROSSFADE}`,
                }}
              />
            </g>
          );
        })}
      </g>

      {bands.map((band) => (
        <circle
          key={`stroke-${band.base.key}`}
          cx={CENTER}
          cy={CENTER}
          r={band.r}
          fill="none"
          stroke="rgb(125,59,168)"
          style={{
            strokeDasharray: 'none',
            strokeWidth: band.strokeWidth,
            strokeOpacity: band.strokeOpacity,
            transition: `r 600ms ${EASE}, stroke-opacity ${RING_CROSSFADE}, stroke-width ${RING_CROSSFADE}`,
          }}
        />
      ))}
    </>
  );
}

function HeadingCone({ compassReady }) {
  const h = (22 * Math.PI) / 180;
  const R = RADAR_RING_OUTER;
  const p = (s) =>
    `${(CENTER + R * Math.sin(s * h)).toFixed(1)},${(CENTER - R * Math.cos(s * h)).toFixed(1)}`;
  return (
    <path
      d={`M${CENTER},${CENTER} L${p(-1)} A${R},${R} 0 0 1 ${p(1)} Z`}
      fill="rgb(125,59,168)"
      style={{ opacity: compassReady ? 0.07 : 0, transition: 'opacity 500ms ease' }}
    />
  );
}

/**
 * You as the bubble (Sam's pick D): the map pin's straight-down form, no nub (Sam, 2026-11-02), the same size as a friend,
 * with the map's light swelling inside it (pinDraft selfPinCanvas `glow`, here a CSS animation).
 */
function SelfBubble({ look }) {
  const [mark, setMark] = useState(null);
  useEffect(() => {
    if (look.bubble !== 5 || mark) return;
    const img = new Image();
    img.onload = () => setMark(img);
    img.src = markUrl;
  }, [look.bubble, mark]);
  const pin = useMemo(
    () => selfPinCanvas({ bubble: look.bubble, stand: look.stand, flat: true, mark }),
    [look.bubble, look.stand, mark],
  );
  const lightId = useId().replace(/:/g, '');
  // the canvas is 13 x PERSON_PIN.k px of radius; the radar draws it at a friend's radius, centred on you
  const k = PEER_AVATAR_R / (13 * 1.5);
  return (
    <g pointerEvents="none">
      <image
        href={pin.canvas.toDataURL()}
        x={CENTER - (pin.w * k) / 2}
        y={CENTER - pin.cy * k}
        width={pin.w * k}
        height={pin.h * k}
      />
      <defs>
        <radialGradient id={lightId}>
          <stop offset="0" stopColor="#f0e2ff" stopOpacity={0.85} />
          <stop offset="0.45" stopColor="#c89bff" stopOpacity={0.65} />
          <stop offset="1" stopColor="#c89bff" stopOpacity={0} />
        </radialGradient>
      </defs>
      <circle
        cx={CENTER}
        cy={CENTER}
        r={PEER_AVATAR_R * 0.9}
        fill={`url(#${lightId})`}
        className="prii-self-light"
      />
    </g>
  );
}

function CenterMarker() {
  const look = useSelfLook();
  if (look) return <SelfBubble look={look} />;
  return (
    <g pointerEvents="none">
      <circle
        cx={CENTER}
        cy={CENTER}
        r={8}
        className="fill-primary"
        style={{
          transformOrigin: `${CENTER}px ${CENTER}px`,
          animation: `priiPulse 2.6s ${EASE} infinite`,
        }}
      />
      <circle cx={CENTER} cy={CENTER} r={8} className="fill-primary" />
      <circle
        cx={CENTER}
        cy={CENTER}
        r={14}
        fill="none"
        className="stroke-primary"
        strokeOpacity={0.4}
        strokeWidth={0.5}
      />
    </g>
  );
}

/**
 * One bubble per friend, always under its own id: joining or leaving a cluster changes how it is drawn, never
 * remounts it, so it cannot jump. Position is written by the radar's frame loop (useRadarMotion), not by React.
 */
function PeerAvatar({ peer, initialTransform, role, onSelect, registerNode }) {
  const hidden = role === 'hidden';
  return (
    <g
      data-peer-id={peer.id}
      ref={(node) => registerNode(peer.id, node)}
      transform={initialTransform}
      onClick={(e) => {
        e.stopPropagation();
        if (!hidden) {
          onSelect(peer.id);
        }
      }}
      style={{
        cursor: hidden ? 'default' : 'pointer',
        opacity: hidden ? 0 : peer.amenityKind ? amenityOpacity(peer) : 1,
        pointerEvents: hidden ? 'none' : undefined,
        transition: 'opacity 300ms ease',
      }}
    >
      {/* An amenity's tap area fits its disc: the face's (31) let a disc swallow taps meant for its neighbour, and a tap
          on the picked one released it - back to the friend you had (Sam, 2026-10-26: the restroom picked Jo). */}
      <circle r={peer.amenityKind ? AMENITY_HIT_R : PEER_HIT_R} fill="#000" opacity={0.001} />
      {peer.isTracked && (peer.amenityKind || selStyle() === 'a') && (
        <circle
          r={peer.amenityKind ? AMENITY_TRACKED_RING_R : PEER_TRACKED_RING_R}
          fill="none"
          stroke="#9a49d2"
          strokeWidth={1}
          strokeOpacity={0.75}
        />
      )}
      {/* the selection style for a tracked friend (lib/map/faceStyle.js selStyle): b their own colour's glow (the look),
          c the selected amenity's white halo, d both */}
      {peer.isTracked && !peer.amenityKind && selStyle() !== 'a' && (
        <TrackedHalo style={selStyle()} colour={ringColor(peer.displayName)} />
      )}
      {peer.amenityKind ? (
        <AmenityFace kind={peer.amenityKind} />
      ) : (
        <RadarPeerFace photoUrl={peer.photoUrl} displayName={peer.displayName} />
      )}
    </g>
  );
}

function pickClusterRepresentatives(cluster) {
  return pickSharedRepresentatives(cluster.peers, {
    nameOf: (p) => p.peer.displayName,
    isTracked: (p) => Boolean(p.peer.isTracked),
  });
}

/** Count badge drawn over a cluster's front bubble; follows that bubble in the frame loop. */
function ClusterBadge({ frontId, count, initialTransform, registerBadge }) {
  return (
    <g
      ref={(node) => registerBadge(frontId, node)}
      transform={initialTransform}
      pointerEvents="none"
      style={{ animation: 'tr-badge-in 220ms ease-out' }}
    >
      <g transform={`translate(${PEER_BADGE_OFFSET}, ${-PEER_BADGE_OFFSET})`}>
        <circle r={PEER_BADGE_R + 1.5} fill={PEER_SKIN.casing} />
        <circle r={PEER_BADGE_R} fill="#9a49d2" stroke="rgb(217 184 240 / 0.7)" strokeWidth={0.8} />
        <text
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={9}
          fontWeight={700}
          fill="#ffffff"
          pointerEvents="none"
        >
          {count}
        </text>
      </g>
    </g>
  );
}

const ROLE_ORDER = { hidden: 0, back: 1, single: 2, front: 3 };

const translateOf = ({ x, y }) => `translate(${x.toFixed(2)}, ${y.toFixed(2)})`;

function prefersReducedMotion() {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

/**
 * Per-peer spring on (radius, angle), stepped every animation frame while anything is still moving. Writes the
 * transforms straight to the bubble, badge and arrow nodes so a 60 fps glide costs no React renders.
 */
function useRadarMotion(targets, trackedId) {
  const motionsRef = useRef(new Map());
  const nodesRef = useRef(new Map());
  const badgesRef = useRef(new Map());
  const arrowRef = useRef({ group: null, line: null, tip: null });
  const targetsRef = useRef(targets);
  const trackedRef = useRef(trackedId);
  const frameRef = useRef(0);
  const lastFrameRef = useRef(0);

  targetsRef.current = targets;
  trackedRef.current = trackedId;

  const paint = useCallback(() => {
    const motions = motionsRef.current;
    for (const [id, node] of nodesRef.current) {
      const m = motions.get(id);
      if (m && node) node.setAttribute('transform', translateOf(motionScreenPosition(m)));
    }
    for (const [id, node] of badgesRef.current) {
      const m = motions.get(id);
      if (m && node) node.setAttribute('transform', translateOf(motionScreenPosition(m)));
    }
    const { group, tip } = arrowRef.current;
    const tracked = trackedRef.current ? motions.get(trackedRef.current) : null;
    if (group) {
      if (!tracked) {
        group.setAttribute('opacity', '0');
      } else {
        const geometry = computeTargetArrow({
          peerScreenX: 0,
          peerScreenY: 0,
          visualRadius: tracked.r,
          screenAngleRad: tracked.a,
          show: true,
          ...ARROW_HEAD,
        });
        // The whole arrow fades as one: stem and head each see-through let the stem show through the head.
        const opacity = geometry.visible ? tracked.arrow * targetArrowRoomFactor(tracked.r) : 0;
        group.setAttribute('opacity', opacity.toFixed(3));
        if (geometry.visible && tip) {
          // Every stem layer (outline, body, the moving light) runs the same span.
          for (const l of group.querySelectorAll('line')) {
            l.setAttribute('x1', geometry.x1);
            l.setAttribute('y1', geometry.y1);
            l.setAttribute('x2', geometry.stemX2 ?? geometry.x2);
            l.setAttribute('y2', geometry.stemY2 ?? geometry.y2);
          }
          tip.setAttribute('d', geometry.tipPath);
          arrowRef.current.tipEdge?.setAttribute('d', geometry.tipPath);
        }
      }
    }
  }, []);

  const tick = useCallback(
    (frameNow) => {
      const now = typeof frameNow === 'number' ? frameNow : performance.now();
      const dt = lastFrameRef.current ? (now - lastFrameRef.current) / 1000 : 1 / 60;
      lastFrameRef.current = now;
      const reduced = prefersReducedMotion();
      const motions = motionsRef.current;
      const live = new Set();
      let settled = true;
      for (const target of targetsRef.current) {
        live.add(target.id);
        let m = motions.get(target.id);
        if (!m || reduced) {
          m = createPeerMotion(target);
          motions.set(target.id, m);
        } else {
          stepPeerMotion(m, target, now, dt);
        }
        if (!isPeerMotionSettled(m, target)) settled = false;
      }
      for (const id of motions.keys()) {
        if (!live.has(id)) motions.delete(id);
      }
      paint();
      if (settled) {
        frameRef.current = 0;
        lastFrameRef.current = 0;
        return;
      }
      frameRef.current = window.requestAnimationFrame(tick);
    },
    [paint],
  );

  // New targets: (re)start the loop. It stops itself once every bubble has come to rest.
  useEffect(() => {
    if (!frameRef.current) {
      lastFrameRef.current = 0;
      frameRef.current = window.requestAnimationFrame(tick);
    }
  }, [targets, trackedId, tick]);

  useEffect(
    () => () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    },
    [],
  );

  const registerNode = useCallback((id, node) => {
    if (node) nodesRef.current.set(id, node);
    else nodesRef.current.delete(id);
  }, []);
  const registerBadge = useCallback((id, node) => {
    if (node) badgesRef.current.set(id, node);
    else badgesRef.current.delete(id);
  }, []);
  const registerArrow = useCallback(
    (part, node) => {
      arrowRef.current[part] = node;
      if (node) paint();
    },
    [paint],
  );

  /** Where a node should start on (re)render: the live spring position, or the target before it has one. */
  const transformFor = useCallback((target) => {
    const m = motionsRef.current.get(target.id);
    return translateOf(m ? motionScreenPosition(m) : { x: target.screenX, y: target.screenY });
  }, []);

  return { registerNode, registerBadge, registerArrow, transformFor };
}

function readoutMain(
  _compassReady,
  tracked,
  {
    peerCount,
    confirmedEmpty,
    peersReady,
    peersHydration,
    subscriptionStatus,
    livePeersSynced,
    blockedReason,
  },
) {
  if (tracked?.amenityKind) {
    return tracked.displayName;
  }
  if (tracked) {
    const name = isUuidLikeString(tracked.displayName)
      ? FIND_PEER_FALLBACK_LABEL
      : tracked.displayName || FIND_PEER_FALLBACK_LABEL;
    if (tracked.presenceKind === 'ble-in-range') {
      // An RSSI band, never a bearing: "Within 20 ft", or just nearby at the edge of Bluetooth range.
      const band = bleNearbyLabel(tracked.distanceMeters);
      return `${name} · ${band === 'Nearby' ? 'nearby' : band.charAt(0).toLowerCase() + band.slice(1)}`;
    }
    if (tracked.distanceMeters != null && Number.isFinite(tracked.distanceMeters)) {
      const distanceLabel = formatFindPeerDistanceWithWalkTime(tracked.distanceMeters, {
        peerId: tracked.id,
        uwbPrecise: tracked.presenceKind === 'uwb-live',
        // Never the GPS floor on a UWB range (2026-10-04).
        horizontalAccuracyMeters:
          tracked.presenceKind === 'uwb-live' ? null : (tracked.horizontalAccuracyMeters ?? null),
      });
      // No "precise" tag and no green: how we measured is ours, not the reader's (operator, 2026-10-12).
      return `${name} · ${distanceLabel}`;
    }
    return name;
  }
  if (blockedReason) {
    return blockedReason;
  }
  if (!peersReady) {
    if (subscriptionStatus === 'error') {
      return 'Connection issue';
    }
    if (peerCount > 0 && peersHydration === 'snapshot') {
      return 'Updating…';
    }
    return 'Connecting…';
  }
  if (!livePeersSynced || peersHydration === 'snapshot') {
    if (peerCount > 0) {
      return 'Updating…';
    }
    return 'Connecting…';
  }
  if (peerCount === 0 && confirmedEmpty) {
    return 'Nobody here'; // the one empty state on Find (Sam, 2026-10-21)
  }
  if (peerCount === 0) {
    return null;
  }
  return 'Tap a user';
}

function readoutSub(_compassReady, tracked, trackedUwbReading, _headingNeedsTap, _headingDenied) {
  if (!tracked) {
    return null;
  }
  // An amenity: how far, with the walk (the radar cannot say past 60 m); a place has no fix to age.
  if (tracked.amenityKind) {
    return tracked.distanceMeters != null
      ? formatFindDistanceWithWalkTime(tracked.distanceMeters)
      : null;
  }
  const now = Date.now();
  // Only the age of a GPS fix earns a second line; a live range or "nearby" says it all in the first.
  if (
    tracked.presenceKind === 'uwb-live' ||
    trackedUwbReading?.active ||
    tracked.presenceKind === 'ble-in-range'
  ) {
    return null;
  }
  if (tracked.gpsLastKnownAt != null) {
    return formatStalenessShort(tracked.gpsLastKnownAt, now);
  }
  return null;
}

function compassSubtext(compassReady, headingNeedsTap, headingDenied) {
  if (compassReady || headingDenied) {
    return null;
  }
  if (headingNeedsTap) {
    return 'Tap to enable compass for direction';
  }
  return 'Compass calibrating…';
}

export default function TrackingRadar({
  peers,
  trackedPeerId,
  trackedUwbReading = null,
  compassReady,
  onPeerTap,
  deviceHeadingDeg,
  headingNeedsTap,
  headingDenied,
  onRequestCompass,
  subscriptionStatus = 'idle',
  livePeersSynced = false,
  peersHydration = 'none',
  isNative = false,
  blockedReason = null,
  sessionKey = null,
}) {
  const useSelfLookOn = Boolean(useSelfLook()); // you as the bubble pin: drawn after the arrow, standing on it
  const glowId = `tr-arrow-glow-${useId().replace(/:/g, '')}`;
  const containerRef = useRef(null);
  const [expandedClusterId, setExpandedClusterId] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [graceElapsed, setGraceElapsed] = useState(false);

  const EMPTY_CONFIRM_GRACE_MS = 2500;

  useEffect(() => {
    setGraceElapsed(false);
    const timer = window.setTimeout(() => setGraceElapsed(true), EMPTY_CONFIRM_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [sessionKey]);

  const peersReady = livePeersSynced || !isNative;

  const showHydratedSnapshot =
    isNative && !peersReady && peers.length > 0 && peersHydration === 'snapshot';

  const confirmedEmpty =
    !peers.some((p) => !p.amenityKind) &&
    graceElapsed &&
    peersReady &&
    (subscriptionStatus === 'connected' || !isNative);

  const showPeerMarkers = peers.length > 0;

  const readoutContext = useMemo(
    () => ({
      peerCount: peers.filter((p) => !p.amenityKind).length, // amenities are not people
      confirmedEmpty,
      peersReady,
      peersHydration,
      subscriptionStatus,
      trackedUwbReading,
      livePeersSynced,
      blockedReason,
    }),
    [
      peers.length,
      confirmedEmpty,
      peersReady,
      peersHydration,
      subscriptionStatus,
      trackedUwbReading,
      livePeersSynced,
      blockedReason,
    ],
  );

  const radarPeers = useMemo(
    () => mapFindRowsToRadarPeers(peers, trackedPeerId),
    [peers, trackedPeerId],
  );

  const trackedPeer = useMemo(
    () => radarPeers.find((peer) => peer.isTracked) ?? null,
    [radarPeers],
  );

  // Last trusted bearing per friend: a low-trust or missing bearing keeps them where they were, not at 12 o'clock.
  const bearingHoldRef = useRef(new Map());

  const positioned = useMemo(() => {
    const hold = bearingHoldRef.current;
    const live = new Set(radarPeers.map((peer) => peer.id));
    for (const id of hold.keys()) {
      if (!live.has(id)) hold.delete(id);
    }
    return radarPeers.map((peer) => {
      const arrowAllowed = peer.isTracked && compassReady && shouldShowTargetArrow(peer);
      const target = resolveRadarTarget(peer, deviceHeadingDeg, hold, arrowAllowed);
      return { ...peer, target, screenX: target.screenX, screenY: target.screenY };
    });
  }, [radarPeers, deviceHeadingDeg, compassReady]);

  const motionTargets = useMemo(() => positioned.map((peer) => peer.target), [positioned]);
  const { registerNode, registerBadge, registerArrow, transformFor } = useRadarMotion(
    showPeerMarkers ? motionTargets : [],
    trackedPeer?.id ?? null,
  );

  const clusters = useMemo(
    () =>
      computeClusters(
        positioned.map((peer) => ({ peer, screenX: peer.screenX, screenY: peer.screenY })),
        RADAR_CLUSTER_THRESHOLD,
        // Each bubble's own size: a face, or the smaller amenity disc.
        (pp) => (pp.peer.amenityKind ? AMENITY_R + 1.2 : PEER_SKIN.casingR),
      ),
    [positioned],
  );

  const multiClusters = useMemo(
    () => (showPeerMarkers ? clusters.filter((cluster) => cluster.peers.length >= 2) : []),
    [clusters, showPeerMarkers],
  );

  /**
   * Clustering only restyles bubbles: the front two of a cluster stay drawn where they are, the rest fade out, and a
   * count badge rides on the front one. Nobody is unmounted, so nobody jumps.
   */
  const clusterLayout = useMemo(() => {
    const roleById = new Map();
    const badges = [];
    for (const cluster of multiClusters) {
      const clusterKey = clusterIdFromPeers(cluster.peers);
      const [front, back] = pickClusterRepresentatives(cluster);
      // A third stays drawn behind the two (Sam, 2026-10-26): with two, tracking one of a three-way group pushed another
      // out of sight - Restrooms vanished when he picked Rideshare Hub. Only a fourth and on hide behind the badge.
      const third = cluster.peers.find((pp) => pp !== front && pp !== back) ?? null;
      for (const pp of cluster.peers) {
        const role = pp === front ? 'front' : pp === back || pp === third ? 'back' : 'hidden';
        roleById.set(pp.peer.id, { role, clusterKey });
      }
      badges.push({
        frontId: front.peer.id,
        clusterKey,
        count: cluster.peers.length,
        target: front.peer.target,
      });
    }
    return { roleById, badges };
  }, [multiClusters]);

  const drawnPeers = useMemo(() => {
    if (!showPeerMarkers) return [];
    const roleOf = (peer) => clusterLayout.roleById.get(peer.id)?.role ?? 'single';
    // Draw order only (React moves the nodes, it does not remount them): fronts over their backs; then amenities after
    // faces, so a disc beside a friend is not under the friend's wide tap area.
    return [...positioned].sort(
      (a, b) =>
        ROLE_ORDER[roleOf(a)] - ROLE_ORDER[roleOf(b)] ||
        Number(Boolean(a.amenityKind)) - Number(Boolean(b.amenityKind)),
    );
  }, [positioned, clusterLayout, showPeerMarkers]);

  // The tracked friend's resolved target: what the drawn arrow aims at (held bearing, trust fade and all). The dev
  // diagnostics read this, not a recomputation with the pre-spring rules (2026-10-16 audit).
  const trackedTarget = useMemo(
    () =>
      trackedPeer ? (positioned.find((peer) => peer.id === trackedPeer.id)?.target ?? null) : null,
    [positioned, trackedPeer],
  );

  const readoutMainText = useMemo(
    () => readoutMain(compassReady, trackedPeer, readoutContext),
    [compassReady, trackedPeer, readoutContext],
  );
  const readoutSubText = useMemo(() => {
    const trackedSub = readoutSub(
      compassReady,
      trackedPeer,
      trackedUwbReading,
      headingNeedsTap,
      headingDenied,
    );
    if (trackedSub) {
      return trackedSub;
    }
    if (!livePeersSynced) {
      return null;
    }
    return compassSubtext(compassReady, headingNeedsTap, headingDenied);
  }, [
    compassReady,
    trackedPeer,
    trackedUwbReading,
    headingNeedsTap,
    headingDenied,
    livePeersSynced,
  ]);

  useEffect(() => {
    if (!trackedPeer || !trackedTarget) {
      return;
    }
    logTargetArrowDiagnostics(trackedPeer, deviceHeadingDeg, trackedTarget);
  }, [trackedPeer, deviceHeadingDeg, trackedTarget]);

  const closePopover = useCallback(() => {
    setExpandedClusterId(null);
    setPopoverAnchor(null);
  }, []);

  const handleClusterToggleRef = useRef(null);
  const handlePeerSelect = useCallback(
    (peerId) => {
      const inCluster = clusterLayout.roleById.get(peerId);
      if (inCluster) {
        handleClusterToggleRef.current?.(inCluster.clusterKey);
        return;
      }
      onPeerTap?.(peerId);
    },
    [clusterLayout, onPeerTap],
  );

  const handleClusterToggle = useCallback(
    (clusterKey) => {
      if (expandedClusterId === clusterKey) {
        closePopover();
        return;
      }
      const cluster = multiClusters.find((c) => clusterIdFromPeers(c.peers) === clusterKey);
      if (!cluster || !containerRef.current) {
        return;
      }
      const rect = containerRef.current.getBoundingClientRect();
      const scale = rect.width / VIEWBOX;
      setExpandedClusterId(clusterKey);
      setPopoverAnchor({
        left: cluster.centerX * scale,
        top: cluster.centerY * scale,
        containerHeight: rect.height,
      });
    },
    [expandedClusterId, multiClusters, closePopover],
  );
  handleClusterToggleRef.current = handleClusterToggle;

  useEffect(() => {
    if (!expandedClusterId) {
      return undefined;
    }
    const onDocClick = (event) => {
      if (containerRef.current?.contains(event.target)) {
        return;
      }
      closePopover();
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [expandedClusterId, closePopover]);

  const expandedCluster = useMemo(
    () =>
      expandedClusterId
        ? (multiClusters.find((c) => clusterIdFromPeers(c.peers) === expandedClusterId) ?? null)
        : null,
    [expandedClusterId, multiClusters],
  );

  const showCompassHint = headingNeedsTap || headingDenied;

  return (
    <div ref={containerRef} className="relative w-full" style={{ aspectRatio: WRAPPER_ASPECT }}>
      <svg
        viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
        className="absolute left-0 top-0 block w-full touch-pan-y"
        // A group's count badge on a bubble at the very top sits a few units past the box (Sam, 2026-10-26: the "4" was
        // clipped under the chips); the radar draws past its edge rather than cutting it.
        style={{ overflow: 'visible' }}
        role="img"
        aria-label="Nearby peers radar"
        onClick={closePopover}
      >
        <HeadingCone compassReady={compassReady} />
        <Rings distanceMeters={trackedPeer?.distanceMeters ?? null} />
        {!useSelfLookOn && <CenterMarker />}

        <g opacity={showHydratedSnapshot ? 0.72 : 1} style={{ transition: 'opacity 300ms ease' }}>
          {drawnPeers.map((peer) => (
            <PeerAvatar
              key={peer.id}
              peer={peer}
              role={clusterLayout.roleById.get(peer.id)?.role ?? 'single'}
              initialTransform={transformFor(peer.target)}
              onSelect={handlePeerSelect}
              registerNode={registerNode}
            />
          ))}

          {clusterLayout.badges.map((badge) => (
            <ClusterBadge
              key={badge.frontId}
              frontId={badge.frontId}
              count={badge.count}
              initialTransform={transformFor(badge.target)}
              registerBadge={registerBadge}
            />
          ))}
        </g>

        {trackedPeer && (
          <g ref={(node) => registerArrow('group', node)} pointerEvents="none" opacity={0}>
            {/* The map's guide arrow (peopleLayer ARROW): lavender with a soft glow of itself - a blurred copy under each
                dash and under the head (was a dark-purple edge, Sam's 4 of 2026-10-26; the map's look, 2026-11-02). */}
            <defs>
              <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2.2" />
              </filter>
            </defs>
            <line className="tr-target-arrow tr-target-arrow--edge" filter={`url(#${glowId})`} />
            <line ref={(node) => registerArrow('line', node)} className="tr-target-arrow" />
            <path
              ref={(node) => registerArrow('tipEdge', node)}
              className="tr-target-arrow-tip tr-target-arrow-tip--edge"
              filter={`url(#${glowId})`}
            />
            <path ref={(node) => registerArrow('tip', node)} className="tr-target-arrow-tip" />
          </g>
        )}
        {/* You stand on your arrow, as on the map */}
        {useSelfLookOn && <CenterMarker />}
      </svg>

      <CompassHint
        show={showCompassHint}
        denied={headingDenied}
        onTap={() => onRequestCompass?.()}
      />

      {expandedCluster && popoverAnchor && (
        <div
          className="radar-cluster-pop absolute z-30"
          style={{
            left: popoverAnchor.left,
            top:
              popoverAnchor.top < popoverAnchor.containerHeight / 2
                ? popoverAnchor.top + 36
                : undefined,
            bottom:
              popoverAnchor.top >= popoverAnchor.containerHeight / 2
                ? popoverAnchor.containerHeight - popoverAnchor.top + 36
                : undefined,
            transform: 'translateX(-50%)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* The Maps page's results list (Sam, 2026-10-26: the old solid card read dated): glass, one row each. */}
          <ul>
            {expandedCluster.peers.map(({ peer }) => (
              <li key={peer.id}>
                <button
                  type="button"
                  onClick={() => {
                    closePopover();
                    onPeerTap?.(peer.id);
                  }}
                  className={`radar-cluster-pop__row${peer.isTracked ? ' radar-cluster-pop__row--on' : ''}`}
                >
                  {peer.amenityKind ? (
                    <span
                      className="radar-cluster-pop__disc"
                      style={{ background: KIND_DISPLAY[peer.amenityKind]?.color }}
                      dangerouslySetInnerHTML={{ __html: iconMarkup(peer.amenityKind, 14) }}
                    />
                  ) : (
                    <Avatar
                      displayName={peer.displayName}
                      profilePhotoUrl={peer.photoUrl}
                      size="xxs"
                      className="!h-7 !w-7"
                    />
                  )}
                  <span className="truncate">
                    {isUuidLikeString(peer.displayName)
                      ? FIND_PEER_FALLBACK_LABEL
                      : peer.displayName || FIND_PEER_FALLBACK_LABEL}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-0.5 pb-0 pt-0">
        {(() => {
          const main = readoutMainText;
          const sub = readoutSubText;
          if (!main && !sub) {
            return null;
          }
          const showTrackedMain = Boolean(trackedPeer);
          const subtextColor =
            !trackedPeer?.amenityKind && trackedPeer?.gpsLastKnownAt != null
              ? stalenessSubtextColor(trackedPeer.gpsLastKnownAt)
              : undefined;
          return (
            <>
              {main && (
                <div
                  className={`max-w-[88%] truncate text-title font-semibold leading-tight tracking-[0.2px] transition-colors ${
                    showTrackedMain
                      ? 'text-ink'
                      : trackedPeer?.presenceKind === 'ble-in-range'
                        ? 'text-accent-400'
                        : 'text-dark-text'
                  }`}
                >
                  {main}
                </div>
              )}
              {sub && (
                <div
                  className="font-mono text-meta leading-none tracking-[0.4px] text-dark-muted"
                  style={subtextColor ? { color: subtextColor } : undefined}
                >
                  {sub}
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}
